import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import clsx from 'clsx'
import { UnmuteButton } from 'components/set/InteractionPlayer/UnmuteButton/UnmuteButton'
import { getAssetIdFromUrl } from 'utils/mux'
import { useStyles } from './styles'
import 'assets/lib/player-ui.css'
import './TheoPlayerStyles.css'
import { Mux } from 'data/VideoPlayer'
import { getActiveMuxStream } from 'redux/slices/liveShow'
import { useDispatch } from 'react-redux'
import { onPlayerEnds } from 'redux/slices/liveShow/thunks'

interface VideoPlayerLiveProps {
    viewerId: string
    videoTitle: string
    muted?: boolean
}

interface PlayerError {
    status: string
    fileType: string
    url: string
    startTime?: number
}

const WAIT_BACKOFF_MS = 1500

const VideoPlayerLive: React.FC<VideoPlayerLiveProps> = ({ viewerId, videoTitle, muted = false }) => {
    const player: any = useRef<any>(null)
    const timeoutErrorId = useRef<any>(null)
    const exponentialTime = useRef<number>(1)
    const [hasError, setHasError] = useState(false)
    const muxMonitor = useRef<Mux | null>(null)
    const dispatch = useDispatch()

    const videoNode = useRef(null)

    const [videoMuted, setVideoMuted] = useState(false)
    const activeMuxStream = useSelector(getActiveMuxStream)

    const httpErrorInterceptor = response => {
        if (response.status >= 400 && response.status <= 599) {
            const errorDetails: PlayerError = {
                status: response.status,
                fileType: response.request.type,
                url: response.request.url,
            }
            const networkErrorEvent = new CustomEvent<PlayerError>('THEOnetworkerror', { detail: errorDetails })

            document.dispatchEvent(networkErrorEvent)
        }
    }

    const setMuted = useCallback(
        muted => {
            if (player.current) {
                player.current.muted = muted
            }
            setVideoMuted(muted)
        },
        [player],
    )

    useEffect(() => {
        // Instantiate Video.js
        if (player.current) return

        if (videoNode.current) {
            player.current = new window.THEOplayer.Player(videoNode.current, {
                libraryLocation: process.env.REACT_APP_THEO_PLAYER_LIBRARY_LOCATION,
                mutedAutoplay: 'all',
                pip: {
                    visibility: 0.7,
                    position: 'bottom-left',
                    retainPresentationModeOnSourceChange: true,
                },
                ui: {
                    fluid: true,
                },
            })

            player.current.addEventListener('ended', () => {
                dispatch(onPlayerEnds())
            })

            player.current.autoplay = true

            muxMonitor.current = window.initTHEOplayerMux(player.current, {
                debug: false,
                data: {
                    env_key: process.env.REACT_APP_MUX_ENV_KEY,
                    viewer_user_id: viewerId,
                    video_title: `${videoTitle}-live`,
                },
            })
        }

        return () => {
            clearTimeout(timeoutErrorId.current)

            if (player.current) {
                player.current.destroy()
            }
        }
    }, [videoTitle, viewerId, dispatch])

    useEffect(() => {
        if (player.current) {
            player.current.addEventListener('volumechange', () => {
                const playerMuted = player.current.muted
                setMuted(playerMuted)
            })
        }
    }, [setMuted])

    useEffect(() => {
        if (player.current) {
            const firstPlaying = () => {
                player.current.removeEventListener('playing', firstPlaying)

                window.heap.track('Player Ready', {
                    'Stream ID': getAssetIdFromUrl(activeMuxStream.muxStreamUrl),
                })
            }

            player.current.addEventListener('sourcechange', () => {
                player.current.removeEventListener('playing', firstPlaying)
                player.current.addEventListener('playing', firstPlaying)
            })

            player.current.addEventListener('error', error => {
                window.heap.track('Videojs Error in VideoPlayerLive', { error })
            })

            player.current.network.addResponseInterceptor(httpErrorInterceptor)

            document.addEventListener('THEOnetworkerror', async ({ detail }: CustomEventInit) => {
                window.heap.track('Videojs Error in VideoPlayerLive', { error: detail })
                setHasError(true)

                if (!timeoutErrorId.current) {
                    player.current.src = activeMuxStream.muxStreamUrl
                }

                const current = exponentialTime.current
                const next = current + current
                exponentialTime.current = next

                timeoutErrorId.current = setTimeout(() => {
                    player.current.src = activeMuxStream.muxStreamUrl
                }, current * WAIT_BACKOFF_MS)
            })

            player.current.addEventListener('playing', () => {
                if (timeoutErrorId.current) {
                    clearTimeout(timeoutErrorId.current)
                    timeoutErrorId.current = null
                    setHasError(false)
                }
            })

            player.current.source = {
                sources: [
                    {
                        src: activeMuxStream.muxStreamUrl,
                        type: 'application/x-mpegurl',
                    },
                ],
            }
        }
    }, [player, activeMuxStream, muted, setMuted, dispatch])

    useEffect(() => {
        // do not emmit the videoChange if the mux monitor or the user does not exist
        if (!muxMonitor.current || !viewerId) return

        muxMonitor.current.emit('videochange', {
            env_key: process.env.REACT_APP_MUX_ENV_KEY,
            viewer_user_id: viewerId,
            video_title: `${videoTitle}-live`,
        })
    }, [viewerId, player, videoTitle, activeMuxStream, muxMonitor])

    const classes = useStyles()

    return (
        <div>
            <div>{videoMuted && <UnmuteButton onClick={() => setMuted(false)} />}</div>
            {hasError && (
                <div className={clsx(classes.internetConnectionIssue)}>
                    <p>Connecting... please hold tight</p>
                </div>
            )}
            <div ref={videoNode} className="theoplayer-container video-js theoplayer-skin vjs-16-9 THEOplayer"></div>
        </div>
    )
}

export default React.memo(VideoPlayerLive)
